// ─── RouteSummaryDialog — Resumo visual da rota em fullscreen ────────────
//
// Apresenta a rota como "boarding pass" com timeline de cidades,
// bandeiras, travessias e custos. Pode ser impresso ou exportado em
// PDF (via jsPDF, gerado no browser sem ida ao backend).
//
// Estilo deliberadamente diferente do RouteDetails — focado em
// apresentação ao cliente, não na operação. Cores menos saturadas,
// tipografia mais generosa.

import React, { useMemo, useState, useEffect, useContext } from 'react';
import { Dialog, Box, Typography, Button, IconButton, Divider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import PrintIcon from '@material-ui/icons/Print';
import QRCode from 'qrcode';
import { useSelector } from 'react-redux';
import { selectTollData } from '../../store/slices/tollsSlice';
import { extractRouteHighlights, countryFlag, countryName } from '../../utils/extractRouteHighlights';
import { exportRouteSummaryPdf } from '../../utils/exportRouteSummaryPdf';
import { AuthContext } from '../../App';

// Cores das barras do mini-mapa termal — palette distinguível para
// até ~8 países simultâneos (rotas EU típicas raramente passam disso).
const COUNTRY_BAR_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#0ea5e9', '#ec4899', '#14b8a6',
];

// Botão "Copiar link" usado no card do QR. Mostra check + label
// "Copiado!" durante 1.5s ao clicar; depois volta ao estado original.
function CopyLinkButton({ url, lang = 'pt' }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  function handleCopy() {
    try {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    } catch {
      // fallback: select text manually (rare)
    }
  }
  return (
    <Box style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <button onClick={handleCopy}
        style={{
          width: '100%',
          padding: '6px 10px',
          background: copied ? '#16a34a' : '#0f172a',
          color: '#fff', border: 'none', borderRadius: 8,
          fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
          transition: 'background 0.2s',
        }}>
        {copied
          ? (lang === 'pt' ? '✓ Copiado!' : '✓ Copied!')
          : (lang === 'pt' ? '📋 Copiar link' : '📋 Copy link')}
      </button>
      <Typography style={{ fontSize: '0.55rem', color: '#cbd5e1', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>
        {url}
      </Typography>
    </Box>
  );
}

// Enviar o link de tracking público por WhatsApp. Deep link wa.me: abre o
// WhatsApp (Web no PC, App no telemóvel) com a mensagem já escrita — o operador
// só escolhe o contacto (ou já vai o número do cliente da OT, se existir) e
// carrega em enviar. Não envia sozinho — é a forma gratuita, sem API Business.
function WhatsAppButton({ url, route, lang = 'pt' }) {
  if (!url) return null;
  const pt = lang === 'pt';
  const origin = route?.origin?.name?.split(',')[0] || '';
  const dest = route?.destination?.name?.split(',')[0] || '';
  const label = origin && dest ? `${origin} → ${dest}` : (route?.name || '');
  const msg = pt
    ? `Acompanhe a rota ${label} em tempo real:\n${url}`
    : `Track the ${label} route in real time:\n${url}`;
  // Telefone do cliente/destinatário, se a rota/OT o trouxer.
  const rawPhone = route?.clientPhone || route?.customerPhone || route?.contactPhone
    || route?.recipientPhone || route?.deliveryPhone || '';
  const phone = String(rawPhone).replace(/[^\d+]/g, '').replace(/^\+/, '');
  const href = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
    : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  return (
    <button onClick={() => window.open(href, '_blank', 'noopener')}
      style={{
        width: '100%', padding: '7px 10px', background: '#25D366',
        color: '#fff', border: 'none', borderRadius: 8,
        fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
      }}>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="#fff"><path d="M17.5 14.4c-.3-.15-1.75-.86-2-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.16-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.53.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.66-1.6-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.75-.71 2-1.4.24-.7.24-1.28.17-1.4-.07-.13-.27-.2-.56-.35zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.1.81.83-3.02-.2-.31A8.2 8.2 0 1 1 12 20.2z"/></svg>
      {pt ? 'Enviar por WhatsApp' : 'Send via WhatsApp'}
    </button>
  );
}

function fmtMoney(v, cur = 'EUR') {
  if (v == null || isNaN(v)) return '—';
  // Valores grandes (moedas fracas tipo AOA) sem casas decimais, como o card do mapa.
  const dp = Math.abs(v) >= 1000 ? 0 : 2;
  return `${Number(v).toLocaleString('pt-PT', { minimumFractionDigits: dp, maximumFractionDigits: dp })} ${cur}`;
}
function fmtDuration(min) {
  if (min == null || isNaN(min)) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h${m > 0 ? ` ${m}m` : ''}`;
  }
  return `${h}h${m > 0 ? ` ${String(m).padStart(2, '0')}` : ''}`;
}

const VEHICLE_ICONS = {
  cisterna: '🛢',
  frigorifico: '❄️',
  light_truck: '🚚',
  general_cargo: '📦',
  car: '🚗',
};
const VEHICLE_LABELS = {
  cisterna: 'Camião Cisterna',
  frigorifico: 'Camião Frigorífico',
  light_truck: 'Camião Ligeiro',
  general_cargo: 'Mercadorias',
  car: 'Carro Ligeiro',
};

const KIND_META = {
  origin:      { icon: '🚩', color: '#10b981', label: 'Origem' },
  destination: { icon: '🏁', color: '#ef4444', label: 'Destino' },
  border:      { icon: '🌍', color: '#6366f1', label: 'Fronteira' },
  crossing:    { icon: '⛴️', color: '#0ea5e9', label: 'Travessia' },
  city:        { icon: '🏙️', color: '#94a3b8', label: 'Cidade' },
  waypoint:    { icon: '📍', color: '#f59e0b', label: 'Paragem' },
  service:     { icon: '🔧', color: '#f59e0b', label: 'Serviço' },
  pickup:      { icon: '📦', color: '#f59e0b', label: 'Recolha' },
};

export default function RouteSummaryDialog({ open, onClose, route, fuelData, lang = 'pt' }) {
  const tollData = useSelector(selectTollData);
  const { token: authToken, modules, clientSettings } = useContext(AuthContext);
  // Tracking público (QR + link) — módulo `public_tracking`. Sem ele nem
  // pedimos o token ao servidor. Fail-open sem lista, igual ao ModuleGate.
  const hasPublicTracking = !modules || modules.length === 0 || modules.includes('public_tracking');
  const pt = lang === 'pt';
  const cr = route?.computedRoute;
  const crossings = tollData?.crossings || [];

  // País por defeito vem SEMPRE das Preferências (clientSettings.defaultCountry),
  // usado como fallback autoritário quando os routeSteps não têm país fiável.
  const defaultCountry = clientSettings?.defaultCountry || '';
  const highlights = useMemo(
    () => extractRouteHighlights(route, crossings, { defaultCountry }),
    [route, crossings, defaultCountry],
  );

  // Mini-mapa termal — barras por país baseadas em countryDistances.
  // Ordenado por km decrescente. Cores ciclam pela palette.
  const countryBars = useMemo(() => {
    const cd = cr?.countryDistances || {};
    const entries = Object.entries(cd)
      .map(([cc, distM]) => ({ cc, km: distM / 1000 }))
      .filter(e => e.km > 0)
      .sort((a, b) => b.km - a.km);
    const totalKm = entries.reduce((s, e) => s + e.km, 0) || 1;
    return entries.map((e, i) => ({
      ...e,
      pct: (e.km / totalKm) * 100,
      color: COUNTRY_BAR_COLORS[i % COUNTRY_BAR_COLORS.length],
    }));
  }, [cr]);

  // QR Code de tracking — só aparece se a rota tem ID + está despachada.
  // O id no estado Redux vem prefixado com 'api-' quando carregado da
  // API (`api-288`); o backend espera o numeric ID puro (`288`). dbId
  // também pode existir directo do payload. Normaliza aqui.
  const dbRouteId = useMemo(() => {
    if (route.dbId) return route.dbId;
    if (typeof route.id === 'string') {
      const m = route.id.match(/^api-(\d+)$/);
      if (m) return parseInt(m[1]);
      const n = parseInt(route.id);
      return Number.isFinite(n) ? n : null;
    }
    return typeof route.id === 'number' ? route.id : null;
  }, [route.id, route.dbId]);

  // O link partilhável (QR + WhatsApp) existe para qualquer rota GUARDADA
  // (dbRouteId) com o módulo de tracking público — o backend assina qualquer
  // rota, não exige despacho. A viatura ao vivo só aparece na página pública
  // depois do despacho, mas o link/QR já servem para partilhar o plano e a ETA
  // com o cliente logo no planeamento.
  const canShareTracking = !!dbRouteId;

  const [trackingUrl, setTrackingUrl] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  useEffect(() => {
    if (!open || !canShareTracking || !authToken || !dbRouteId || !hasPublicTracking) {
      setTrackingUrl(null); setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    fetch(`/planner-api/tracking/sign/${dbRouteId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d?.token) return;
        // Hash routing — não bate no servidor, é só client-side
        const url = `${window.location.origin}/#/track/${dbRouteId}?t=${encodeURIComponent(d.token)}`;
        setTrackingUrl(url);
        return QRCode.toDataURL(url, {
          width: 256, margin: 1,
          color: { dark: '#0f172a', light: '#ffffff' },
        });
      })
      .then(dataUrl => { if (dataUrl && !cancelled) setQrDataUrl(dataUrl); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, canShareTracking, dbRouteId, authToken, hasPublicTracking]);

  if (!route || !cr) return null;

  const totalKm = (cr.distance || 0) / 1000;
  const totalMin = Math.round((cr.duration || 0) / 60);
  const subType = route.routeOptions?.vehicleSubType || (route.routeOptions?.vehicleType === 'car' ? 'car' : 'cisterna');
  // Custos base estão em EUR (combustível, portagens, travessias). Convertê-los
  // para a moeda de exibição com a MESMA taxa que o card do mapa (conv = eur*rate),
  // senão o resumo mostra o valor em EUR com o rótulo da moeda local (ex.: 46 AOA
  // em vez de 60186 Kz). Os custos personalizados já são escritos na moeda de
  // exibição — não voltar a converter.
  const rate = fuelData?.currencyRate || 1;
  const fuelCost = (fuelData?.totalCostEUR || 0) * rate;
  const crossingsCost = crossings.reduce((s, c) => s + c.cost, 0) * rate;
  const tollCost = ((tollData?.totalCost || 0) - crossings.reduce((s, c) => s + c.cost, 0)) * rate;
  const customCost = (route.customCosts || []).reduce((s, c) => s + (Number(c.value) || 0), 0);
  const total = fuelCost + tollCost + crossingsCost + customCost;

  const cur = fuelData?.currencySymbol || fuelData?.currencyCode || 'EUR';

  function handlePdf() {
    exportRouteSummaryPdf({
      highlights, route,
      fuelCost, tollCost, crossingsCost, customCost, total,
      currency: cur,
      vehicle: {
        plate: route.vehiclePlate,
        subType,
        label: VEHICLE_LABELS[subType] || subType,
      },
      driver: route.driverName ? { name: route.driverName } : null,
      countryCosts: fuelData?.countryRows || [],
      countryBars,
      qrDataUrl,
      trackingUrl,
      lang,
    });
  }
  function handlePrint() { window.print(); }

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ style: { background: '#f8fafc' } }}>
      {/* Toolbar */}
      <Box style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 24px', background: '#0f172a', color: '#fff',
      }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Typography style={{ fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.04em' }}>
            ECOOGPS
          </Typography>
          <Box style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.2)' }} />
          <Typography style={{ fontSize: '0.78rem', opacity: 0.85 }}>
            {pt ? 'Resumo de viagem' : 'Trip summary'}
          </Typography>
        </Box>
        <Box style={{ display: 'flex', gap: 8 }}>
          <Button startIcon={<PrintIcon style={{ fontSize: 16 }} />}
            onClick={handlePrint}
            style={{
              color: '#fff', textTransform: 'none', fontSize: '0.78rem',
              background: 'rgba(255,255,255,0.1)', padding: '6px 14px', borderRadius: 8,
            }}>
            {pt ? 'Imprimir' : 'Print'}
          </Button>
          <Button startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
            onClick={handlePdf}
            style={{
              color: '#0f172a', textTransform: 'none', fontSize: '0.78rem',
              background: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700,
            }}>
            PDF
          </Button>
          <IconButton onClick={onClose} style={{ color: '#fff', padding: 6 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box className="route-summary-printable" style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }}>
        <Box style={{ maxWidth: 880, margin: '0 auto' }}>

          {/* Header — vehicle + route */}
          <Box style={{
            background: '#fff', borderRadius: 14,
            border: '1px solid #e2e8f0',
            padding: '24px 28px', marginBottom: 18,
            boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <Box style={{
                width: 64, height: 64, borderRadius: 16,
                background: 'linear-gradient(135deg, #6366f1, #4338ca)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 32, flexShrink: 0,
              }}>
                {VEHICLE_ICONS[subType] || '🚛'}
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                  {route.origin?.name?.split(',')[0]} <span style={{ color: '#cbd5e1' }}>→</span> {route.destination?.name?.split(',')[0]}
                </Typography>
                <Typography style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>
                  {VEHICLE_LABELS[subType] || subType}
                  {route.vehiclePlate && ` · ${route.vehiclePlate}`}
                  {route.driverName && ` · ${route.driverName}`}
                </Typography>
              </Box>
              <Box style={{ textAlign: 'right' }}>
                <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {pt ? 'Custo total' : 'Total cost'}
                </Typography>
                <Typography style={{ fontSize: '1.8rem', fontWeight: 800, color: '#16a34a', lineHeight: 1.1, marginTop: 2 }}>
                  {fmtMoney(total, cur)}
                </Typography>
                <Typography style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                  {totalKm > 0 ? `${(total / totalKm).toFixed(3)} ${cur}/km` : ''}
                </Typography>
              </Box>
            </Box>

            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 18 }}>
              <Box style={{ padding: '10px 14px', background: '#f1f5f9', borderRadius: 10 }}>
                <Typography style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {pt ? 'Distância' : 'Distance'}
                </Typography>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                  {totalKm.toFixed(0)} km
                </Typography>
              </Box>
              <Box style={{ padding: '10px 14px', background: '#f1f5f9', borderRadius: 10 }}>
                <Typography style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {pt ? 'Duração' : 'Duration'}
                </Typography>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                  {fmtDuration(totalMin)}
                </Typography>
              </Box>
              <Box style={{ padding: '10px 14px', background: '#f1f5f9', borderRadius: 10 }}>
                <Typography style={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {pt ? 'Pontos chave' : 'Key points'}
                </Typography>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                  {highlights.length}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Mini-mapa termal + QR Code lado a lado */}
          {(countryBars.length > 0 || qrDataUrl) && (
            <Box style={{
              display: 'grid',
              gridTemplateColumns: qrDataUrl ? '1fr 200px' : '1fr',
              gap: 16, marginBottom: 18,
            }}>
              {countryBars.length > 0 && (
                <Box style={{
                  background: '#fff', borderRadius: 14,
                  border: '1px solid #e2e8f0',
                  padding: '20px 24px',
                }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                    {pt ? '🌐 Distribuição por país' : '🌐 Distribution by country'}
                  </Typography>
                  {countryBars.map(b => (
                    <Box key={b.cc} style={{ marginBottom: 10 }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.95rem' }}>{countryFlag(b.cc)}</span>
                          <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
                            {countryName(b.cc)}
                          </Typography>
                          <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                            {b.pct.toFixed(1)}%
                          </Typography>
                        </Box>
                        <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569' }}>
                          {b.km.toFixed(0)} km
                        </Typography>
                      </Box>
                      <Box style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                        <Box style={{
                          width: `${b.pct}%`, height: '100%',
                          background: `linear-gradient(90deg, ${b.color}, ${b.color}dd)`,
                          borderRadius: 999,
                          transition: 'width 0.3s ease',
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {qrDataUrl && (
                <Box style={{
                  background: '#fff', borderRadius: 14,
                  border: '1px solid #e2e8f0',
                  padding: '16px 14px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 8,
                }}>
                  <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {pt ? 'Tracking' : 'Tracking'}
                  </Typography>
                  <Box style={{
                    padding: 6, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 10,
                  }}>
                    <img src={qrDataUrl} alt="QR Code"
                      style={{ display: 'block', width: 140, height: 140 }} />
                  </Box>
                  <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>
                    {pt ? 'Aponta a câmara' : 'Scan to track'}
                  </Typography>

                  {/* Enviar por WhatsApp + copiar link — para partilhar com o
                      cliente final ou testar no PC sem precisar de scanner. */}
                  <WhatsAppButton url={trackingUrl} route={route} lang={lang} />
                  <CopyLinkButton url={trackingUrl} lang={lang} />
                </Box>
              )}
            </Box>
          )}

          {/* Timeline */}
          <Box style={{
            background: '#fff', borderRadius: 14,
            border: '1px solid #e2e8f0',
            padding: '24px 28px', marginBottom: 18,
          }}>
            <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              {pt ? '📍 Itinerário' : '📍 Itinerary'}
            </Typography>
            <Box style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Linha vertical */}
              <Box style={{ position: 'absolute', left: 11, top: 12, bottom: 12, width: 2, background: 'linear-gradient(to bottom, #10b981, #cbd5e1 50%, #ef4444)' }} />

              {highlights.map((h, i) => {
                const meta = KIND_META[h.kind] || KIND_META.city;
                return (
                  <Box key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    paddingBottom: 18, position: 'relative',
                  }}>
                    {/* Dot */}
                    <Box style={{
                      position: 'absolute', left: -28, top: 2,
                      width: 24, height: 24, borderRadius: '50%',
                      background: '#fff', border: `3px solid ${meta.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>
                      {h.kind === 'crossing' ? '⛴️' : h.country ? countryFlag(h.country) : meta.icon}
                    </Box>

                    {/* Conteúdo */}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Box style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <Typography style={{
                          fontSize: h.kind === 'origin' || h.kind === 'destination' ? '1.05rem' : '0.92rem',
                          fontWeight: h.kind === 'origin' || h.kind === 'destination' ? 800 : 700,
                          color: '#0f172a',
                        }}>
                          {h.name}
                        </Typography>
                        {h.country && h.kind !== 'border' && (
                          <Typography style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            {countryName(h.country)}
                          </Typography>
                        )}
                      </Box>
                      {(h.distKm != null || h.durationMin != null || h.cost != null) && (
                        <Box style={{ display: 'flex', gap: 12, marginTop: 4, alignItems: 'center' }}>
                          {h.distKm != null && (
                            <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
                              📏 {h.distKm.toFixed(0)} km
                            </Typography>
                          )}
                          {h.durationMin != null && h.durationMin > 0 && (
                            <Typography style={{ fontSize: '0.7rem', color: '#64748b' }}>
                              ⏱ {fmtDuration(h.durationMin)}
                            </Typography>
                          )}
                          {h.cost != null && (
                            <Typography style={{ fontSize: '0.72rem', color: '#0ea5e9', fontWeight: 700 }}>
                              {fmtMoney(h.cost, h.currency)}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Breakdown de custos */}
          <Box style={{
            background: '#fff', borderRadius: 14,
            border: '1px solid #e2e8f0',
            padding: '24px 28px', marginBottom: 18,
          }}>
            <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              {pt ? '💰 Breakdown de custos' : '💰 Cost breakdown'}
            </Typography>
            {[
              { label: pt ? '⛽ Combustível' : '⛽ Fuel', value: fuelCost, color: '#f59e0b' },
              { label: pt ? '🪙 Portagens' : '🪙 Tolls', value: tollCost, color: '#8b5cf6' },
              { label: pt ? '⛴️ Travessias / Eurotúnel' : '⛴️ Crossings', value: crossingsCost, color: '#0ea5e9' },
              { label: pt ? '💼 Custos adicionais' : '💼 Other', value: customCost, color: '#64748b' },
            ].filter(r => r.value > 0).map((r, i) => (
              <Box key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #f1f5f9',
              }}>
                <Typography style={{ fontSize: '0.86rem', color: '#0f172a' }}>{r.label}</Typography>
                <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: r.color }}>
                  {fmtMoney(r.value, cur)}
                </Typography>
              </Box>
            ))}
            <Box style={{
              marginTop: 14, padding: '14px 16px',
              background: 'linear-gradient(135deg, #0f172a, #1e293b)',
              borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Typography style={{ fontSize: '0.76rem', color: '#fff', fontWeight: 800, letterSpacing: '0.04em' }}>
                {pt ? 'TOTAL' : 'TOTAL'}
              </Typography>
              <Typography style={{ fontSize: '1.4rem', color: '#fff', fontWeight: 800 }}>
                {fmtMoney(total, cur)}
              </Typography>
            </Box>
          </Box>

          <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', textAlign: 'center', fontStyle: 'italic' }}>
            {pt
              ? `Os valores apresentados são estimativas baseadas em tarifas correntes. EcooGPS · Gerado em ${new Date().toLocaleString('pt-PT')}`
              : `Estimated values based on current tariffs. EcooGPS · Generated ${new Date().toLocaleString('en-GB')}`}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}
